import * as Axios from 'axios'
import store from '../store/index'
import router from '@/router'
import { getTimeDiff } from '@/util/jwt-util'

// Testing helpers: artificially shorten perceived token lifetime here as well
// to match auth module behavior. Set to 0 to disable.
const ARTIFICIAL_MAX_VALIDITY_MS = 2 * 60 * 1000 // 2 minutes
const RENEWAL_TIME_BUFFER_MS = 5000

var baseURL = '/api'
if (process.env.NODE_ENV === 'development') {
  baseURL = 'http://localhost:8000/api'
}
// process.env.VUE_APP_BASE_URL | ''
// Development: Log base URL for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('BASE URL ', baseURL)
}

// Make Axios play nice with Django CSRF
Axios.defaults.xsrfCookieName = 'csrftoken'
Axios.defaults.xsrfHeaderName = 'X-CSRFToken'

const httpClient = Axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=utf-8;',
  },
})

const authInterceptor = async (config) => {
  // Skip auth handling for token refresh endpoint to avoid circular dependency
  const isRefreshRequest = config.url && config.url.includes('token/refresh')
  if (isRefreshRequest) {
    return config
  }

  const expire = store.getters['auth/expire']
  const isRefreshing = store.getters['auth/isRefreshing']
  const refreshPromise = store.getters['auth/refreshPromise']

  // Check if token expires soon (within 10 seconds)
  if (expire) {
    const rawTimeDiff = getTimeDiff(expire)
    const timeDiff = ARTIFICIAL_MAX_VALIDITY_MS && ARTIFICIAL_MAX_VALIDITY_MS > 0 ? Math.min(rawTimeDiff, ARTIFICIAL_MAX_VALIDITY_MS) : rawTimeDiff
    // Token expires soon or already expired
    if (timeDiff < 10000) {
      if (isRefreshing && refreshPromise) {
        // Token refresh already in progress, wait for it
        try {
          await refreshPromise
        } catch (error) {
          // Refresh failed, will be handled by response interceptor
          console.error('Token refresh failed in request interceptor:', error)
        }
      } else if (timeDiff < RENEWAL_TIME_BUFFER_MS) {
        // Token expires very soon and no refresh in progress
        // Trigger refresh proactively
        try {
          await store.dispatch('auth/refreshToken')
        } catch (error) {
          console.error('Proactive token refresh failed:', error)
        }
      }
    }
  }

  // Add current token to request
  const token = store.getters['auth/accessToken']
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
}

httpClient.buildQuery = function buildQuery(q = { search: "", conditions: [], page: 1, ordering: null }) {
  let query = ''
  if (q.search != null && q.search.length > 0) {
    query = `search=${q.search}`
  }
  for (const condition of q.conditions) {
    if (query.length > 0) {
      query += "&"
    }
    query += `${condition}`
  }
  if (query.length > 0) {
    query += "&"
  }
  query += `page=${q.page}`
  if (q.ordering) {
    if (query.length > 0) {
      query += "&"
    }
    query += `ordering=${q.ordering}`
  }
  // console.log('building query :>> ', query)
  return query
}

httpClient.interceptors.request.use(authInterceptor)

httpClient.interceptors.response.use((response) => response,
  async (error) => {
    const { config, response } = error
    const { status } = response || {}

    // Skip 401 handling for token refresh endpoint - if refresh fails, just reject
    const isRefreshRequest = config?.url && config.url.includes('token/refresh')
    if (status === 401 && isRefreshRequest) {
      return Promise.reject(error)
    }

    if (status === 401) {
      // Check if token refresh is already in progress
      const isRefreshing = store.getters['auth/isRefreshing']
      const refreshPromise = store.getters['auth/refreshPromise']

      if (isRefreshing && refreshPromise) {
        // Wait for the ongoing refresh to complete, then retry the request
        try {
          await refreshPromise
          // Token refreshed successfully, retry the original request
          const token = store.getters['auth/accessToken']
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
          }
          return httpClient(config)
        } catch (refreshError) {
          // Token refresh failed, logout will be triggered
          return Promise.reject(error)
        }
      } else {
        // No refresh in progress, trigger logout
        await store.dispatch('auth/logout', null, { root: true })
      }
    }
    return Promise.reject(error)
  }
)

httpClient.interceptors.response.use(
  response => {
    return response
  },
  error => {
    // Log errors in development mode only
    if (process.env.NODE_ENV === 'development') {
      console.error('HTTP Client Error:', error);
    }
    if (process.env.NODE_ENV === 'development') {
      return Promise.reject(error);
    }
    if (!error.response) {
      // Network error - show user-friendly message
      if (process.env.NODE_ENV === 'development') {
        console.log("Please check your internet connection.");
      }
      router.push('/pages/error/?status=Serwis%20jest%20niedostepny')
      return Promise.reject(error)
    } else if (error.response.status > 401) {
      router.push(`/pages/error/?status=${error.response.status}`)
      return Promise.reject(error);
    } else if (error.response.status > 409) {
      router.push(`/pages/error/?status=${error.response.status}`);
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  }
)

export default httpClient
